import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Button, Radio, Space} from 'antd';
import {
  StyledSelectCardTypeModal,
  StyledSelectCardTypeModalRadio,
  StyledSelectCardTypeModalRadioContent
} from './StyledSelectCardTypeModal';
import {cardTypesConstants, companyFeaturesConstants} from '../../../../constants';
import {useFeatureAvailable} from '../../../../hooks';

import {ReactComponent as DigitalOptionImg} from '../../../../static/images/pages/cards/new-card-option-digital.svg';
import {ReactComponent as PhysicalOptionImg} from '../../../../static/images/pages/cards/new-card-option-physical.svg';
import {ReactComponent as SubscriptionOptionImg} from '../../../../static/images/pages/cards/new-card-option-subscription.svg';

const cardOptions = [
  {
    color: '#07132B',
    img: <PhysicalOptionImg />,
    label: 'physical',
    value: cardTypesConstants.PHYSICAL
  },
  {
    color: '#3AF2AE',
    img: <DigitalOptionImg />,
    label: 'digital',
    value: cardTypesConstants.DIGITAL
  },
  {
    color: '#40B0D5',
    img: <SubscriptionOptionImg />,
    label: 'subscription',
    value: cardTypesConstants.SUBSCRIPTION
  }
];

const SelectCardTypeModal = ({
  onOk,
  open,
  ...rest
}) => {
  const [trans] = useTranslation(['main', 'cards']);
  const [variant, setVariant] = useState(null);

  const isPhysicalAvailable = useFeatureAvailable(companyFeaturesConstants.PHYSICAL_CARD_AVAILABLE);

  const filteredOptions = useMemo(
    () =>
      cardOptions.filter(
        (option) => option.value !== cardTypesConstants.PHYSICAL || isPhysicalAvailable
      ),
    [isPhysicalAvailable]
  );

  const windowWidth = useMemo(() => {
    const optionWidth = 212;
    const spaceWidth = 27;
    const sidePadding = 40 * 2; // 40px on each side
    const count = filteredOptions.length;
    if (count === 0) return sidePadding;
    return (optionWidth * count) + (spaceWidth * (count - 1)) + sidePadding;
  }, [filteredOptions]);

  const t = (key, options) => trans(`cards:modal.selectCardType.${key}`, options);

  const tOption = (key) => t(`variant.${key}`);

  const handleContinue = () => onOk && onOk(variant);

  const afterClose = () => {
    if (variant) setVariant(null)
  }

  const footer = [
    <Button
      disabled={!variant}
      key='submit'
      onClick={handleContinue}
      type='primary'
      size='large'
    >
      {trans('continue')}
    </Button>
  ];

  return (
    <StyledSelectCardTypeModal
      afterClose={afterClose}
      centered={true}
      footer={footer}
      open={open}
      width={windowWidth}
      {...rest}
    >
      <Space size={27} align='start'>
        {filteredOptions.map(option => {
          const label = option?.label;
          const value = option?.value;
          const checked = variant === value;
          return (
            <StyledSelectCardTypeModalRadio
              color={option?.color}
              key={`option-${value}`}
              direction='vertical'
              size={18}
            >
              <StyledSelectCardTypeModalRadioContent
                align='end'
                className={checked && 'checked'}
                direction='vertical'
                size='small'
                onClick={() => setVariant(value)}
              >
                <Radio
                  checked={checked}
                  key={label}
                  value={option.value}
                />
                {option.img}
              </StyledSelectCardTypeModalRadioContent>

              <Space direction='vertical' size='small'>
                <h3>{tOption(`${label}.title`)}</h3>
                <p>{tOption(`${label}.description`)}</p>
              </Space>
            </StyledSelectCardTypeModalRadio>
          )
        })}
      </Space>
    </StyledSelectCardTypeModal>
  );
}

SelectCardTypeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOk: PropTypes.func
}

export default SelectCardTypeModal;
